import { Button, Collapse, Group, NumberInput, Stack, Switch } from "@mantine/core"
import { IconCloudUpload } from "@tabler/icons-react"

import ModuleSettingsCard from "../module-settings-card/ModuleSettingsCard"
import { ModuleSettingsProps } from "../module-settings-card/ModuleSettingsProps"
import _DeviceUploadModuleSettings from "./_DeviceUploadModuleSettings"
import _GroupUploadModuleSettings from "./_GroupUploadModuleSettings"
import useUploadModuleSettings from "./useUploadModuleSettings"
import SettingsOverwriteWarning from "../settings-overwrite-warning/SettingsOverwriteWarning"
import { useLanguage } from "../../../../contexts/language-context"
import RequiresVersion from "../../../devices/components/require-version/RequiresVersion"
import useInferStatus from "../../../devices/hooks/useInferStatus"
import { ModuleFC } from "../module-settings-card/ModuleFC"
import UploadCommands from "./UploadCommands"

type UploadModuleSettingsProps = ModuleSettingsProps<"limit_to_days_ago" | "upload_minimum_interval">

const UploadModuleSettings: ModuleFC<UploadModuleSettingsProps> = Object.assign(
    ({ attrs, organisation, group, device }) => {
        const { form, onSubmit, reset, inheritSettings, processing } = useUploadModuleSettings(
            attrs,
            organisation,
            group,
            device,
        )

        const { L } = useLanguage()
        const inferStatus = useInferStatus()
        const health = device?.health
        const status = inferStatus(health)

        return (
            <ModuleSettingsCard title={L("Uploading settings")} icon={IconCloudUpload}>
                <form onSubmit={form.onSubmit(onSubmit)}>
                    <Stack>
                        <SettingsOverwriteWarning attrs={attrs} group={group} device={device} />
                        <RequiresVersion device={device} group={group} version="1.12.5">
                            <Switch
                                label={L("Minimum interval between uploads")}
                                description={L(
                                    "Specify the minimum number of minutes that must pass between uploads. Zero means no minimum interval.",
                                )}
                                checked={form.values.onMinimumInterval}
                                {...form.getInputProps("onMinimumInterval")}
                            />
                            <Collapse in={form.values.onMinimumInterval}>
                                <NumberInput
                                    ml="3.375rem"
                                    min={1}
                                    {...form.getInputProps("minimumInterval")}
                                    value={form.values.minimumInterval ?? ""}
                                />
                            </Collapse>
                        </RequiresVersion>
                        <Switch
                            label={L("Limited days to sync uploads")}
                            description={L(
                                "Determine how many days in the past the device may upload recorded videos. More days result in more data usage.",
                            )}
                            checked={form.values.limitDaysToSync}
                            {...form.getInputProps("limitDaysToSync")}
                        />
                        <Collapse in={form.values.limitDaysToSync}>
                            <NumberInput
                                ml="3.375rem"
                                min={1}
                                {...form.getInputProps("daysToSync")}
                                value={form.values.daysToSync ?? ""}
                            />
                        </Collapse>
                        <RequiresVersion device={device} group={group} version="1.17.0" hide={true}>
                            <Switch
                                description={L("Whether the device should only upload when connected to Wi-Fi.")}
                                label={L("Only upload on Wi-Fi")}
                                checked={form.values.onlyUploadWifi}
                                {...form.getInputProps("onlyUploadWifi")}
                            />
                        </RequiresVersion>
                        {device && (
                            <RequiresVersion device={device} group={group} version="1.15.0" hide={true}>
                                <UploadCommands
                                    organisation={organisation}
                                    group={group}
                                    device={device}
                                    disabled={
                                        status !== "online" ||
                                        (health.network !== undefined && health.network !== "wifi")
                                    }
                                />
                            </RequiresVersion>
                        )}
                        <Group position="right" spacing="xs">
                            {device && (
                                <Button
                                    variant="subtle"
                                    onClick={inheritSettings}
                                    disabled={form.values.daysToSync === null && form.values.minimumInterval === null && form.values.onlyUploadWifi === null}
                                >
                                    {L("Apply group settings")}
                                </Button>
                            )}
                            <Button variant="subtle" onClick={reset} disabled={!form.isDirty()}>
                                {L("Reset")}
                            </Button>
                            <Button type="submit" loading={processing} disabled={!form.isDirty()}>
                                {L("Save")}
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </ModuleSettingsCard>
        )
    },
    {
        Device: _DeviceUploadModuleSettings,
        Group: _GroupUploadModuleSettings
    }
)

export default UploadModuleSettings
